import { type FC } from 'react'

import { type PimProductWithVariantsV1 } from '@redteclab/api/clients/bully'

import { Button, Link } from 'base-ui'

import { useGlobalConfigContext } from '../../../global-config/context/globalConfigContext'
import { priceGetFormattedPrice } from '../../../price/priceGetFormattedPrice'
import { useProductPrices } from '../../../product-page/components/ProductBuyBox/hooks/useProductPrices'
import { RetailPrice } from '../../../product-page/components/ProductBuyBox/ProductVariantHeader/RetailPrice'
import { productAccessorGetLowestPrice } from '../../../product-page/model/productAccessor/productAccessorGetLowestPrice'
import {
  EXPERIMENT_TRACK_EVENT_CLICK_ON_CTA,
  experimentsTrackEvent,
} from '../../eventTracking'
import { EXPERIMENT_VARIATION } from '../../model/EXPERIMENT_VARIATION'

import { EXPERIMENT_OWN_BRANDS_OVERLAY_BUTTON_TEXT } from './ExperimentOwnBrandsOverlayConsts'
import { ExperimentOwnBrandsOverlayImage } from './ExperimentOwnBrandsOverlayImage'
import { ExperimentOwnBrandsProductRating } from './ExperimentOwnBrandsProductRating'
import { useExperimentOwnBrandsOverlayProductProperties } from './useExperimentOwnBrandsOverlayProductProperties'

export const ExprimentOwnBrandsOverlayProductV2: FC<{
  ownBrandsProduct: PimProductWithVariantsV1
}> = ({ ownBrandsProduct }) => {
  const {
    currentPrescriptionType,
    href,
    intl,
    name,
    offers,
    rating,
    upid,
    variants,
  } = useExperimentOwnBrandsOverlayProductProperties(ownBrandsProduct)

  const {
    discount,
    displayPrice,
    formattedUnitPrice,
    isDiscountValid,
    strikeThroughOfferPrice,
  } = useProductPrices({
    currentPrescriptionType,
    offerId: offers?.[0]?.offerId,
    product: ownBrandsProduct,
  })
  const globalConfig = useGlobalConfigContext()

  return (
    <section className="flex flex-col pt-2 tablet:pt-0" data-product-id={upid}>
      <div className="relative flex justify-center">
        <ExperimentOwnBrandsOverlayImage
          discount={discount}
          product={ownBrandsProduct}
          variation={EXPERIMENT_VARIATION.V2}
        />
      </div>
      <div className="flex flex-1 flex-col items-start justify-start px-1 tablet:px-2.5">
        <span className="font-medium">{name}</span>
        {rating && rating.count > 0 ? (
          <ExperimentOwnBrandsProductRating
            href={`${href}#user-reviews-section`}
            rating={rating}
          />
        ) : null}
        <div className="flex items-end">
          <RetailPrice
            className="font-bold text-dark-brand tablet:!text-3xl"
            currentPrescriptionType={currentPrescriptionType}
            data-qa-id="product-price"
            isDiscountValid={isDiscountValid}
            isMasterPage={false}
            lowestPrice={productAccessorGetLowestPrice(variants)}
            offerPrice={displayPrice}
          />

          {isDiscountValid && offers ? (
            <div className="pl-1 pt-1.5 text-xs text-dark-primary-strong line-through">
              {priceGetFormattedPrice(
                strikeThroughOfferPrice?.price ?? 0,
                intl,
              )}
            </div>
          ) : null}
        </div>
        <span className="text-xs text-dark-primary-strong">
          {formattedUnitPrice}
        </span>
      </div>
      <Link
        className="mt-2 block w-full"
        href={href}
        onClick={() => {
          experimentsTrackEvent(
            globalConfig,
            EXPERIMENT_TRACK_EVENT_CLICK_ON_CTA,
          )
        }}
      >
        <Button className="w-full" size="medium">
          {EXPERIMENT_OWN_BRANDS_OVERLAY_BUTTON_TEXT[globalConfig.locale]}
        </Button>
      </Link>
    </section>
  )
}
